import { useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import { generateSecurePassword, STRONG_PASSWORD_PATTERN } from "../utils/password.utils";

function buildSchema(messages: { required: string; password: string; mismatch: string }) {
  return z
    .object({
      // One `.regex()` carrying OUR message: a chain of un-messaged rules surfaces Zod's English defaults —
      // including the pattern source — straight into the UI.
      password: z.string().min(1, messages.required).regex(STRONG_PASSWORD_PATTERN, messages.password),
      password_confirm: z.string(),
    })
    .refine((values) => values.password === values.password_confirm, {
      path: ["password_confirm"],
      message: messages.mismatch,
    });
}

export interface SetPasswordSectionProps {
  isBusy: boolean;
  onSubmit: (password: string) => void;
}

/**
 * Setting a password for someone else.
 *
 * Its own `<form>`, sibling to the profile one rather than nested inside it — the accordion panels unmount
 * when collapsed, so each section owns the form it submits and nested `<form>` elements never arise.
 *
 * A distinct section rather than a field mixed into the profile, because its consequences are not what an
 * operator assumes. All three statements below are verified against the service: `PATCH { password }` hashes,
 * stamps `password_changed_at`, and does nothing else. In particular it does NOT revoke the person's sessions
 * — the self-service `/auth/change-password` is the one that does — so an operator resetting a compromised
 * account must suspend first, or the attacker's session survives the reset.
 *
 * The fields are plain text, not masked, and that is deliberate: nothing is emailed, so the operator has to
 * read this value and hand it over through another channel. Masking it would hide the one thing they need.
 */
export function SetPasswordSection({ isBusy, onSubmit }: SetPasswordSectionProps) {
  const { t } = useTranslations("users");

  const schema = useMemo(
    () =>
      buildSchema({
        required: t("validation_required"),
        password: t("password_requirements"),
        mismatch: t("password_mismatch"),
      }),
    [t],
  );

  const form = useZodForm(schema, { password: "", password_confirm: "" });

  const generate = () => {
    const next = generateSecurePassword(16);
    form.setValue("password", next, { shouldValidate: true });
    form.setValue("password_confirm", next, { shouldValidate: true });
  };

  return (
    // No `id`: the submit button lives inside this form, so nothing needs to reference it from outside — the
    // opposite of `CreateUserDrawer`, whose button sits in the drawer footer and needs `form={…}`.
    <ColocaForm
      form={form}
      onSubmit={(values) => onSubmit(values.password)}
      className="flex flex-col gap-3 pt-2"
    >
      {/* The rule lives above the pair, not inside one cell. A helper in a single grid cell makes that
          column taller, and a stretched `FormItem` — a grid of auto rows — redistributes the other column's
          free space between its label and its input, which is exactly how two labels drift out of line.
          `items-start` guards the same class of problem for validation messages. */}
      <p className="text-xs text-muted-foreground">{t("password_requirements")}</p>

      <div className="grid items-start gap-3 sm:grid-cols-2">
        <ColocaInputField name="password" label={t("password_new")} autoComplete="new-password" />
        <ColocaInputField name="password_confirm" label={t("password_confirm")} autoComplete="new-password" />
      </div>

      <ColocaSurface variant="muted" padding="sm" className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t("password_note_sessions")}</span>
        <span className="text-xs text-muted-foreground">{t("password_note_no_force")}</span>
        <span className="text-xs text-muted-foreground">{t("password_note_no_email")}</span>
        <span className="mt-1 text-xs text-warning">{t("password_note_compromised")}</span>
      </ColocaSurface>

      <div className="flex items-center justify-end gap-2">
        <ColocaButton type="button" variant="secondary" size="sm" onClick={generate}>
          {t("create_generate")}
        </ColocaButton>
        <ColocaButton type="submit" variant="primary" size="sm" loading={isBusy}>
          {t("password_submit")}
        </ColocaButton>
      </div>
    </ColocaForm>
  );
}
